// Pending deliveries panel — rendered on Home. Surfaces clients whose
// policy was issued in the last 60 days but no Policy Delivery
// Summary has been marked sent yet. Color-coded by age:
//   - fresh   (≤ 7 days)  : slate     — gentle nudge
//   - aging   (7–21 days) : amber     — getting late
//   - overdue (> 21 days) : red       — needs attention now
//
// Click a row → opens the PolicyDeliveryModal pre-loaded for that
// client. Marking delivered inside the modal refreshes this panel so
// the row disappears. Auto-hides entirely when the queue is empty so
// it never adds visual noise on a "you're caught up" day.

import React from 'react';
import { supabase } from '../lib/supabase';
import PolicyDeliveryModal from './PolicyDeliveryModal';

const WINDOW_DAYS = 60;

function daysSince(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 86_400_000);
  } catch { return null; }
}

function ageBucket(days) {
  if (days == null) return { key: 'fresh', label: 'New',     tone: 'slate',  ringTone: 'slate-200' };
  if (days <= 7)    return { key: 'fresh', label: 'Fresh',   tone: 'slate',  ringTone: 'slate-200' };
  if (days <= 21)   return { key: 'aging', label: 'Aging',   tone: 'amber',  ringTone: 'amber-300' };
  return                   { key: 'overdue', label: 'Overdue', tone: 'red', ringTone: 'red-300' };
}

export default function PendingDeliveriesPanel({ currentUser, settings = {}, showToast = () => {} }) {
  const [clients, setClients] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openClient, setOpenClient] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const cutoff = new Date(Date.now() - WINDOW_DAYS * 86_400_000).toISOString().slice(0, 10);
      const { data } = await supabase
        .from('clients')
        .select('id, name, phone, email, address, policy, carrier, dob, issue_date, first_draft_date, draft_amount, billing_status, delivery_sent_at, delivery_method')
        .eq('owner_id', currentUser.id)
        .is('delivery_sent_at', null)
        .not('issue_date', 'is', null)
        .gte('issue_date', cutoff)
        .order('issue_date', { ascending: true });
      setClients(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  React.useEffect(() => { load(); }, [load]);

  if (loading || clients.length === 0) return null;

  const counts = {
    fresh:   clients.filter(c => (daysSince(c.issue_date) ?? 0) <= 7).length,
    aging:   clients.filter(c => { const d = daysSince(c.issue_date) ?? 0; return d > 7 && d <= 21; }).length,
    overdue: clients.filter(c => (daysSince(c.issue_date) ?? 0) > 21).length,
  };

  return (
    <>
      <div className={`bg-white rounded-xl overflow-hidden ${counts.overdue > 0 ? 'border-2 border-red-300 ring-1 ring-red-100' : 'border border-slate-200'}`}>
        <div className={`px-5 py-4 border-b ${counts.overdue > 0 ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100'}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className={`text-sm font-bold uppercase tracking-tight ${counts.overdue > 0 ? 'text-red-900' : 'text-slate-900'}`}>
                📄 Pending policy deliveries
              </h3>
              <div className={`text-xs mt-0.5 ${counts.overdue > 0 ? 'text-red-800' : 'text-slate-600'}`}>
                {clients.length} polic{clients.length === 1 ? 'y' : 'ies'} issued in the last {WINDOW_DAYS} days · {counts.fresh} fresh · {counts.aging} aging · {counts.overdue} overdue
              </div>
            </div>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {clients.slice(0, 20).map(c => {
            const days = daysSince(c.issue_date);
            const b = ageBucket(days);
            return (
              <div key={c.id} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.carrier && <span>{c.carrier}</span>}
                    {c.policy && <span> · policy {c.policy}</span>}
                    {c.issue_date && (
                      <>
                        <span> · issued {c.issue_date}</span>
                        {days != null && (
                          <span className={`ml-1 font-semibold text-${b.tone}-700`}>
                            ({days}d ago — {b.label})
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setOpenClient(c)}
                    className={`bg-${b.tone}-600 hover:bg-${b.tone}-700 text-white text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded-md`}
                    title="Open the Policy Delivery Summary for this client"
                  >
                    Build delivery
                  </button>
                </div>
              </div>
            );
          })}
          {clients.length > 20 && (
            <div className="px-5 py-2 text-xs text-slate-500 text-center">
              …and {clients.length - 20} more. Send the top batch to surface the rest.
            </div>
          )}
        </div>
      </div>

      <PolicyDeliveryModal
        client={openClient}
        settings={settings}
        open={!!openClient}
        onClose={() => setOpenClient(null)}
        showToast={showToast}
        onDelivered={() => {
          setOpenClient(null);
          load();
        }}
      />
    </>
  );
}
